import React from 'react';
import { useTranslation } from '@/locales';

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

// 默认错误显示组件
function DefaultErrorFallback({ error, resetError }: { error?: Error; resetError: () => void }) {
    const { t } = useTranslation();
    
    return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-red-200 dark:border-red-800">
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <div className="flex-shrink-0">
                            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                {t('error.boundary.title', '页面出现错误')}
                            </h3>
                        </div>
                    </div>
                    
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('error.boundary.description', '抱歉，页面遇到了一个错误。您可以尝试刷新页面或返回首页。')}
                        </p>
                        
                        {error && process.env.NODE_ENV === 'development' && (
                            <details className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('error.boundary.details', '错误详情')}
                                </summary>
                                <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                                    {error.message}
                                    {error.stack && `\n\n${error.stack}`}
                                </pre>
                            </details>
                        )}
                    </div>
                    
                    <div className="flex space-x-3">
                        <button
                            onClick={resetError}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                            {t('error.boundary.retry', '重试')}
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                            {t('error.boundary.home', '返回首页')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // 记录错误信息
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        // 更新状态以包含错误信息
        this.setState({
            error,
            errorInfo
        });
        
        // 调用自定义错误处理函数
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
        
        // 在生产环境中可以发送错误报告到监控服务
        if (process.env.NODE_ENV === 'production') {
            // 这里可以集成错误监控服务，如 Sentry
            // reportError(error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    }

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || DefaultErrorFallback;
            return (
                <FallbackComponent 
                    error={this.state.error} 
                    resetError={this.resetError}
                />
            );
        }

        return this.props.children;
    }
}
